<script lang="ts">
// @ts-ignore - compat: Vue 2 + composition API
import { defineComponent, watch, ref, computed, nextTick, getCurrentInstance, onMounted, onBeforeUnmount } from 'vue';
import { CopyIcon, ThumbsUpIcon, ThumbsDownIcon, Maximize2Icon, Minimize2Icon, ChevronDownIcon } from 'vue-feather-icons';
import { useCopilotChat } from './useCopilotChat';
// @ts-ignore
import avatarUrl from '@/assets/imgs/rose_perfil.svg';
import loadingSvg from '@/assets/imgs/loading.svg';
import ChatHeader from './widgets/ChatHeader.vue';
import ChatMessage from './widgets/ChatMessage.vue';
import ChatLoading from './widgets/ChatLoading.vue';
import ChatInput from './widgets/ChatInput.vue';
import ChatEmptyState from './widgets/ChatEmptyState.vue';
import NewChatButton from './widgets/NewChatButton.vue';
import Notification from '@/shared/components/Notification.vue';
import ResultDialog from './widgets/ResultDialog.vue';
import FeedbackDialog from './widgets/FeedbackDialog.vue';
// @ts-ignore
import { DynamicScroller, DynamicScrollerItem } from 'vue-virtual-scroller';
// @ts-ignore
import 'vue-virtual-scroller/dist/vue-virtual-scroller.css';
import Vue from 'vue';
import { createCopilotService } from '@/infrastructure/services/copilotServiceFactory';
import { clearAllMessages } from '@/infrastructure/idb/indexedDb'
import { communication } from '@/infrastructure/plugins/communication';

type ChatListItem = { 
  id: string; 
  title: string; 
  initial_message: string; 
  date: string; 
  time: string; 
  reference_id: string; 
  created_at: string;
};

export default defineComponent({
  name: 'CopilotChat',
  components: {
    CopyIcon,
    ThumbsUpIcon,
    ThumbsDownIcon,
    Maximize2Icon,
    Minimize2Icon,
    ChevronDownIcon,
    ChatHeader,
    ChatMessage,
    ChatLoading,
    ChatInput,
    ChatEmptyState,
    Notification,
    ResultDialog,
    FeedbackDialog,
    NewChatButton,
    DynamicScroller,
    DynamicScrollerItem,
  },
  props: {
    open: {
      type: Boolean,
      required: true,
    },
    dark: {
      type: Boolean,
      default: false,
    },
    agentName: {
      type: String,
      required: false
    },
    threadId: {
      type: String,
      required: false,
    },
    user_email: {
      type: String,
      required: false,
      default: '',
    },
    requester_id: {
      type: String,
      required: false,
      default: '',
    },
    agentId: {
      type: String,
      required: false,
      default: '1',
    },
    reference_id: {
      type: String,
      required: false,
    },
    stream: {
      type: Boolean,
      default: true,
    },
  },
  emits: ['close'],
  setup(
    props: { 
      open: boolean,
      dark?: boolean,
      threadId: string, 
      user_email: string, 
      requester_id: string,
      agentId: string, 
      reference_id: string,
      stream: boolean,
      agentName?: string,
    },
    context: { emit: (event: string) => void }
  ) {
    const instance = getCurrentInstance()?.proxy;
    const service = createCopilotService();
    const {
      openChat,
      showNotification,
      notificationMessage,
      notificationType,
      handleSend,
      sendDetailedFeedback,
      ...rest
    } = useCopilotChat(
      props.threadId, 
      props.user_email, 
      props.requester_id, 
      props.agentId,
      props.reference_id,
      instance,
      props.stream
    );

    const knowledgeDialogOpen = ref(false);
    const knowledgeDialogContent = ref('');
    const reactionState = ref<Record<string, boolean | null>>({});
    const openFeedbackForMessage = ref<{ message: any; rect?: DOMRect } | null>(null);
    const feedbackContainer = ref<HTMLElement | null>(null);

    const handleShowKnowledgeBase = (source: string) => {
      knowledgeDialogContent.value = source;
      knowledgeDialogOpen.value = true;
    };
    const handleCloseKnowledgeDialog = () => {
      knowledgeDialogOpen.value = false;
    };

    const handleClose = () => {
      context.emit('close');
    };

    watch(
      () => props.open,
      (newVal: boolean) => {
        if (newVal) openChat();
      }
    );

    // Inicializa o estado de reação com base nas mensagens
    watch(
      () => (rest as any).messages.value.length,
      (newLen: number, oldLen: number | undefined) => {
        const msgs = (rest as any).messages.value as any[];
        if (!Array.isArray(msgs) || newLen === 0) return;

        const startIndex = oldLen ? oldLen : 0;
        for (let i = startIndex; i < newLen; i++) {
          const msg = msgs[i];
          if (msg && msg.sender === 'assistant') {
            (Vue as any).set(reactionState.value, msg.id, msg.response_feedback);
          }
        }

        visibleCount.value = msgs.length;
      },
      { immediate: true }
    );

    function likeMessage(msg: any) {
      (Vue as any).set(reactionState.value, msg.id, true);
      rest.likeMessage(msg);
    }
    function dislikeMessage(msg: any) {
      (Vue as any).set(reactionState.value, msg.id, false);
      rest.dislikeMessage(msg);
    }

    const handleOpenFeedback = (payload: { message: any; rect?: DOMRect }) => {
      openFeedbackForMessage.value = payload;
    };
    const handleCloseFeedback = () => {
      openFeedbackForMessage.value = null;
    };
    const handleSubmitFeedback = (_payload: any) => {
      sendDetailedFeedback(openFeedbackForMessage.value?.message.id, _payload.description, _payload.tags);
      openFeedbackForMessage.value = null;
    };

    const feedbackPlacement = computed(() => {
      if (!openFeedbackForMessage.value?.rect || !chatBox.value) return 'below' as 'above' | 'below';
      const trigger = openFeedbackForMessage.value.rect;
      const containerRect = chatBox.value.getBoundingClientRect();
      const spaceBelow = containerRect.bottom - trigger.bottom;
      const spaceAbove = trigger.top - containerRect.top;
      return spaceBelow < spaceAbove ? 'above' : 'below';
    });

    const feedbackStyle = computed(() => {
      if (!openFeedbackForMessage.value?.rect || !chatBox.value) return { display: 'none' } as any;
      const trigger = openFeedbackForMessage.value.rect;
      const containerRect = chatBox.value.getBoundingClientRect();
      const maxWidthPx = Math.min(600, containerRect.width - 16);
      const initialLeft = trigger.left - containerRect.left;
      const left = Math.min(
        Math.max(8, initialLeft),
        containerRect.width - maxWidthPx - 8
      );

      if (feedbackPlacement.value === 'above') {
        const bottom = containerRect.bottom - trigger.top + 8;
        return { left: left + 'px', bottom: bottom + 'px', width: maxWidthPx + 'px' } as any;
      }
      const top = trigger.bottom - containerRect.top + 8;
      return { left: left + 'px', top: top + 'px', width: maxWidthPx + 'px' } as any;
    });

    // Fecha ao clicar fora
    const handleGlobalClick = (e: MouseEvent) => {
      if (!openFeedbackForMessage.value) return;
      const container = feedbackContainer.value;
      if (container && container.contains(e.target as Node)) return;
      openFeedbackForMessage.value = null;
    };
    watch(openFeedbackForMessage, (val: { message: any; rect?: DOMRect } | null) => {
      if (val) {
        window.addEventListener('mousedown', handleGlobalClick, { passive: true });
      } else {
        window.removeEventListener('mousedown', handleGlobalClick as any);
      }
    });
    onBeforeUnmount(() => {
      window.removeEventListener('mousedown', handleGlobalClick as any);
    });

    const pageSize = 10;
    const visibleCount = ref(pageSize);
    const loadingMore = ref(false);
    const chatMain = ref<HTMLElement | null>(null);
    const chatBox = ref<HTMLElement | null>(null);
    const isAtBottom = ref(true);
    const showScrollDown = ref(false);
    const expanded = ref(false);
    const loadingThread = ref(false);

    // Raw threads from composable (listThreads response)
    const threadItems = computed(() => {
      const items = ((rest as any).threadsResponse?.value?.data?.items) || [];
      // Filtrar threads duplicados baseado no thread_id
      const uniqueItems = items.filter((item: any, index: number, self: any[]) => 
        index === self.findIndex((t: any) => t.thread_id === item.thread_id)
      );
      return uniqueItems;
    });
    const hasThreads = computed(() => Array.isArray(threadItems.value) && threadItems.value.length > 0);
    // const hasThreads = computed(() => false);
   
    const chatList = ref<ChatListItem[]>([]);
    const selectedChatId = ref<string | null>(null);
    const showNewChatInput = ref(false);
    const startingNewChat = ref(false);
    const firstMessageTitle = ref('');

    const openNewChatInput = () => {
      showNewChatInput.value = true;
      startingNewChat.value = true;
      selectedChatId.value = null; 
      
      firstMessageTitle.value = '';
      communication.emitClearChat();
    };

    const createFirstChatAndSend = async (question: string) => {
      const q = (question || '').trim();
      if (!q) return;
      
      // Limpa o estado atual
      showNewChatInput.value = false;
      startingNewChat.value = false;
      (rest as any).input.value = q;
      
      // Envia a mensagem
      await handleSend();
    };


    const selectChat = async (id: string) => {
      loadingThread.value = true; // Inicia loading
      selectedChatId.value = id;
      showNewChatInput.value = false;
      startingNewChat.value = false;
      try {
        await (rest as any).openThread(id);
        scrollToBottom();
      } finally {
        loadingThread.value = false; // Finaliza loading
      }
    };

    const handleBackToList = async () => {
      selectedChatId.value = null;
      showNewChatInput.value = false;
      startingNewChat.value = false;
      firstMessageTitle.value = '';
      await openChat();
    };

    const visibleMessages = computed(() => {
      if (!rest.messages.value) return [];
      return rest.messages.value.slice(-visibleCount.value);
    });

    // Captura o primeiro texto enviado em uma nova conversa para exibir no header
    watch(
      () => (rest as any).loadingMsg?.value,
      (val: string) => {
        if (startingNewChat.value && val && !firstMessageTitle.value) {
          firstMessageTitle.value = val;
        }
      }
    );

    const renderMessages = computed(() => {
      const msgs = visibleMessages.value;
      const filtered = msgs.filter((msg: any) => {
        if (msg.sender === 'assistant') {
          return msg.content && String(msg.content).trim() !== '';
        }
        return true;
      });

      return filtered.sort((a: any, b: any) => {
        const timeA = a.timestamp ? new Date(a.timestamp).getTime() : 0;
        const timeB = b.timestamp ? new Date(b.timestamp).getTime() : 0;
        return timeA - timeB;
      });
      
    });

    // Função para mostrar mais mensagens mantendo o scroll
    const showMore = async () => {
      if (!chatMain.value) return;
      loadingMore.value = true;
      const prevHeight = chatMain.value.scrollHeight;
      visibleCount.value += pageSize;
      await nextTick();
      const newHeight = chatMain.value.scrollHeight;
      chatMain.value.scrollTop = newHeight - prevHeight;
      loadingMore.value = false;
    };

    const scrollToBottom = () => {
      if (chatMain.value) {
        chatMain.value.scrollTop = chatMain.value.scrollHeight;
        // Esconde o botão imediatamente
        showScrollDown.value = false;
      }
    };

    const handleFormatDate = (input: string | Date | number) => {
      const pad = (n: number) => String(n).padStart(2, '0');
      const parseAsLocal = (str: string) => {
        const s = String(str).trim();
        const m = s.match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?(?:\.\d+)?(?:Z)?$/);
        if (m) {
          const [, y, mo, d, hh, mm, ss = '0'] = m;
          return new Date(
            Number(y),
            Number(mo) - 1,
            Number(d),
            Number(hh),
            Number(mm),
            Number(ss),
            0,
          );
        }
        return new Date(str);
      };

      let d;
      if (input instanceof Date) d = input;
      else if (typeof input === 'string') d = parseAsLocal(input);
      else d = new Date(input);

      const y = d.getFullYear();
      const m = pad(d.getMonth() + 1);
      const day = pad(d.getDate());
      const hh = pad(d.getHours());
      const mm = pad(d.getMinutes());
      return `${day}/${m}/${y} ${hh}:${mm}`;
    };

    // Detecta se está no fundo do chat
    const checkScrollPosition = () => {
      if (!chatMain.value) return;
      const threshold = 200;
      const atBottom = chatMain.value.scrollHeight - chatMain.value.scrollTop - chatMain.value.clientHeight < threshold;
      isAtBottom.value = atBottom;
      showScrollDown.value = !atBottom;
    };

    // Listener de scroll com throttle
    let scrollTimeout: number | null = null;
    let scrollHandler: ((e: Event) => void) | null = null;
    
    const setupScrollListener = () => {
      nextTick(() => {
        if (!chatMain.value) return;
        
        // Remove listener antigo se existir
        if (scrollHandler) {
          chatMain.value.removeEventListener('scroll', scrollHandler as any);
        }
        
        // Cria novo handler
        scrollHandler = () => {
          if (scrollTimeout) clearTimeout(scrollTimeout);
          scrollTimeout = setTimeout(() => {
            checkScrollPosition();
          }, 100) as unknown as number;
        };
        
        // Adiciona listener
        chatMain.value.addEventListener('scroll', scrollHandler, { passive: true });
        
        // Verifica posição inicial
        checkScrollPosition();
      });
    };
    
    // Setup inicial
    onMounted(() => {
      setupScrollListener();
    });
    
    // Re-setup ao trocar de thread ou quando chatMain mudar
    watch(() => selectedChatId.value, () => {
      setTimeout(() => setupScrollListener(), 200);
    });
    
    watch(() => chatMain.value, (newVal) => {
      if (newVal) setupScrollListener();
    });
    
    // Cleanup
    onBeforeUnmount(() => {
      if (chatMain.value && scrollHandler) {
        chatMain.value.removeEventListener('scroll', scrollHandler as any);
      }
      if (scrollTimeout) clearTimeout(scrollTimeout);
    });

    // Atualiza ao adicionar mensagens
    watch(
      () => rest.messages.value.length,
      (newLen: number, oldLen: number) => {
        nextTick(() => {
          checkScrollPosition();
          if (newLen > oldLen && isAtBottom.value) {
            scrollToBottom();
          }
        });
      }
    );

    watch(
      () => (rest as any).currentThreadId?.value,
      (newThreadId: string) => {
        if (newThreadId && startingNewChat.value) {
          selectedChatId.value = newThreadId;
          startingNewChat.value = false;
          const now = new Date();
          const chat: ChatListItem = { 
            id: newThreadId, 
            title: firstMessageTitle.value || (rest as any).input.value || 'Nova conversa', 
            initial_message: firstMessageTitle.value || (rest as any).input.value || 'Nova conversa', 
            date: now.toISOString(), 
            time: now.toISOString(), 
            reference_id: newThreadId, 
            created_at: now.toISOString() 
          };
          chatList.value = [chat, ...chatList.value];
        }
      }
    );

    watch(() => props.open, async (newVal: boolean) => {
      if (newVal) {
        nextTick(() => {
          checkScrollPosition();
        });
      }
    });


    watch(expanded, () => {
      nextTick(() => {
        checkScrollPosition();
        scrollToBottom();
      });
    });

    const headerTitle = computed(() => {
      if (startingNewChat.value) return firstMessageTitle.value || 'Nova conversa';
      if (selectedChatId.value) {
        const t = (threadItems.value as any[]).find((it: any) => it.thread_id === selectedChatId.value);
        if (t?.initial_message) return t.initial_message;
        const item = chatList.value.find((c: ChatListItem) => c.id === selectedChatId.value);
        if (item?.initial_message) return item.initial_message;
        return 'Assistente Rose';
      }
      return '';
    });

    return {
      agentName: props.agentName,
      avatarUrl,
      loadingSvg,
      handleClose,
      showNotification,
      notificationMessage,
      notificationType,
      knowledgeDialogOpen,
      knowledgeDialogContent,
      handleShowKnowledgeBase,
      handleCloseKnowledgeDialog,
      handleSend,
      ...rest,
      reactionState,
      likeMessage,
      dislikeMessage,
      openFeedbackForMessage,
      handleOpenFeedback,
      handleCloseFeedback,
      handleSubmitFeedback,
      feedbackStyle,
      visibleMessages,
      renderMessages,
      showMore,
      visibleCount,
      pageSize,
      loadingMore,
      chatMain,
      chatBox,
      feedbackContainer,
      scrollToBottom,
      showScrollDown,
      expanded,
      Maximize2Icon,
      Minimize2Icon,
      ChevronDownIcon,
      setExpanded: (val: boolean) => (expanded.value = val),
      threadItems,
      hasThreads,
      chatList,
      selectedChatId,
      showNewChatInput,
      loadingThread,
      firstMessageTitle,

      openNewChatInput,
      createFirstChatAndSend,
      selectChat,
      handleBackToList,
      startingNewChat,
      headerTitle,
      handleFormatDate,
    };
  },
});
</script>

<template>
  <div>
    <transition name="fade">
      <div
        v-if="open"
        style="z-index: 99999"
        class="fixed inset-0 flex items-end justify-end pointer-events-none overflow-x-hidden"
      >
        <div
          ref="chatBox"
          :class="[
            'scroll-smooth w-full m-6 rounded-lg border border-[#e3dbf5] flex flex-col pointer-events-auto shadow-2xl overflow-hidden relative bg-[url(@/assets/imgs/area.svg)] bg-cover bg-center bg-white',
            expanded
              ? 'max-w-[600px] w-1/2 min-h-[95vh] h-[95vh]'
              : 'max-w-[340px] 2xl:max-w-[400px] min-h-[90vh] h-[90vh]',
          ]"
        >
          <div v-if="isWaitingPreviousStream && loadingSendingMessage" class="absolute inset-0 z-30 flex flex-col items-center justify-center bg-white/80 dark:bg-gray-900/80">
            <svg class="animate-spin h-10 w-10 mb-4 text-purple-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
            </svg>
            <span class="text-gray-700 dark:text-gray-200 text-base font-medium text-center px-4">Aguardando o término do atendimento anterior para carregar o novo contexto...</span>
          </div>
          <div class="relative">
            <ChatHeader 
              :avatar-url="avatarUrl" 
              :fullHeader="(hasThreads && !selectedChatId && !startingNewChat) || (!hasThreads && !showNewChatInput)" 
              :title="headerTitle" 
              :newChat="startingNewChat" 
              @back="handleBackToList"
            >
              <template #actions>
                <button
                  @click="setExpanded(!expanded)"
                  class="ml-2 text-[#E2E6E9] hover:text-white transition-colors p-1 rounded-full focus:outline-none"
                  aria-label="Expandir chat"
                >
                  <Maximize2Icon v-if="!expanded" width="16" height="16" />
                  <Minimize2Icon v-else width="17" height="17" />
                </button>
                <button
                  @click="handleClose"
                  class="text-[#E2E6E9] hover:text-white transition-colors p-1 rounded-full focus:outline-none ml-1"
                  aria-label="Fechar chat"
                >
                  <svg width="20" height="20" fill="none" viewBox="0 0 24 24">
                    <path stroke="currentColor" stroke-width="2" d="M6 6l12 12M6 18L18 6" />
                  </svg>
                </button>
              </template>
            </ChatHeader>
          </div>

          <!-- EMPTY: sem chats -->
          <template v-if="!hasThreads">
            <div class="flex flex-col h-full">
              <!-- Área principal com scroll -->
              <main
                ref="chatMain"
                class="flex-1 overflow-y-auto"
                style="height: calc(100vh - 200px)"
              >
                <div :class="[
                    openFeedbackForMessage ? 'overflow-hidden' : 'overflow-y-auto',
                    'p-4 space-y-3 scrollbar-thin scrollbar-thumb-purple-200 scrollbar-track-transparent relative overflow-x-hidden'
                  ]">

                  <div v-if="loadingMore" class="flex justify-center items-center py-2">
                    <span class="text-xs text-gray-400">Carregando mais mensagens...</span>
                  </div>
                  <!-- EmptyState centralizado -->
                  <div class="empty-center" v-if="!visibleMessages.length">
                    <ChatEmptyState :username="agentName ?? user_email" />
                  </div>
                  
                  <!-- Mensagens em formato de balões -->
                  <div v-for="(msg, idx) in renderMessages" :key="(msg.id ? msg.id : idx) + '-' + msg.sender" class="mb-2">
                    <ChatMessage
                      :message="msg"
                      :dark="dark"
                      :reaction="reactionState[msg.id]"
                      @copy="copyMessage"
                      @like="likeMessage"
                      @dislike="dislikeMessage"
                      @show-knowledge-base="handleShowKnowledgeBase"
                      @open-feedback="handleOpenFeedback"
                      @scroll-bottom="scrollToBottom"
                    >
                      <div v-if="msg.sender === 'assistant'" v-html="renderMarkdown(msg.content)" class="markdown-rendered" />
                      <div v-else>{{ msg.content }}</div>
                    </ChatMessage>
                  </div>
                  
                  <!-- Render do chat normal -->
                  <ChatLoading
                    v-if="loading"
                    :dark="dark"
                    :msg="loadingMsg"
                    @scroll-bottom="scrollToBottom"
                  />
                </div>
              </main>

              <!-- Área fixa na parte inferior -->
              <div class="flex-none w-full" :class="{ 'bg-transparent': !showNewChatInput && !hasThreads,' bg-white border-t border-gray-200': !showNewChatInput && hasThreads }">
                <ChatInput
                  v-if="showNewChatInput"
                  v-model="input"
                  :loading="loading || loadingSendingMessage"
                  :textarea-ref="textareaRef"
                  :rows="textareaRows"
                  class="bg-white"
                  @send="handleSend"
                />
                <NewChatButton v-else @click.native="openNewChatInput" />
              </div>
            </div>
          </template>

          <!-- LISTA: há threads cruas e nenhum selecionado -->
          <template v-else-if="hasThreads && !selectedChatId && !startingNewChat">
            
              <main style="height: calc(100vh - 240px)" class="bg-white flex-1">
                <DynamicScroller
                  :items="threadItems"
                  key-field="thread_id"
                  :min-item-size="56"
                  class="h-full overflow-y-auto"
                >
                  <template #default="{ item, active, index }">
                    <DynamicScrollerItem :item="item" :active="active">
                      <div class="cursor-pointer p-2 flex items-center justify-between hover:bg-[#F5F6F7]" @click="selectChat(item.thread_id)">
                        <div class="flex flex-col px-4 py-2">
                          <div class="text-sm/[18px] text-[#353949] line-clamp-2 text-ellipsis overflow-hidden w-full">{{ item.initial_message }}</div>
                          <div class="text-[9px]/[16px] text-[#616573]">{{ handleFormatDate(item.created_at) }}</div>
                        </div>
                        <div>
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" class="text-[#666666]">
                            <path d="M9 6l6 6-6 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                          </svg>
                        </div>
                      </div>
                    </DynamicScrollerItem>
                  </template>
                </DynamicScroller>
              </main>
              <div class="flex-none w-full bg-transparent bg-white border-t border-gray-200">
                  <NewChatButton  @click.native="openNewChatInput" />
              </div>
          
          </template>

          <!-- CONVERSA: chat selecionado -->
          <template v-else>
            <!-- Loading overlay quando carregando thread -->
            <div v-if="loadingThread" class="absolute inset-0 z-30 flex flex-col items-center justify-center bg-white/80">
              <svg class="animate-spin h-10 w-10 mb-4 text-purple-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
              </svg>
              <span class="text-gray-700 text-base font-medium text-center px-4">Carregando conversa...</span>
            </div>

            <main
              ref="chatMain"
              class="flex-1 overflow-y-auto"
            >
            <div   :class="[
                openFeedbackForMessage ? 'overflow-hidden' : 'overflow-y-auto',
                'p-4 space-y-3 scrollbar-thin scrollbar-thumb-purple-200 scrollbar-track-transparent relative overflow-x-hidden'
              ]">

              <div v-if="loadingMore" class="flex justify-center items-center py-2">
                <span class="text-xs text-gray-400">Carregando mais mensagens...</span>
              </div>
              
              <div v-for="(msg, idx) in renderMessages" :key="(msg.id ? msg.id : idx) + '-' + msg.sender" class="mb-2">
                <ChatMessage
                  :message="msg"
                  :dark="dark"
                  :reaction="reactionState[msg.id]"
                  @copy="copyMessage"
                  @like="likeMessage"
                  @dislike="dislikeMessage"
                  @show-knowledge-base="handleShowKnowledgeBase"
                  @open-feedback="handleOpenFeedback"
                  @scroll-bottom="scrollToBottom"
                >
                  <div v-if="msg.sender === 'assistant'" v-html="renderMarkdown(msg.content)" class="markdown-rendered" />
                  <div v-else>{{ msg.content }}</div>
                </ChatMessage>
              </div>
                
                  <ChatLoading
                    v-if="loading"
                    :dark="dark"
                    :msg="loadingMsg"
                    @scroll-bottom="scrollToBottom"
                  />
            </div>
            </main>

            <div
              v-if="openFeedbackForMessage && openFeedbackForMessage.message.sender === 'assistant'"
              ref="feedbackContainer"
              class="absolute z-[60] px-2 pointer-events-none left-0 right-0 mx-auto"
              :style="feedbackStyle"
            >
              <div class="pointer-events-auto">
                <FeedbackDialog
                  :show="true"
                  @close="handleCloseFeedback"
                  @submit="handleSubmitFeedback"
                />
              </div>
            </div>

            <div
              v-if="visibleMessages.length > 0"
              ref="copilotChatBottom"
              class="text-[.7rem] text-gray-400 text-center py-[8px] leading-4 bg-[#F5F6F7] border-t border-[#D9D9D9]"
            >
              A Rose é uma IA e pode cometer erros. <br>
              Revise as informações antes de enviar ao cliente.
            </div>

            <button
              v-if="showScrollDown"
              @click="scrollToBottom"
              class="scroll-down-btn bg-white absolute bottom-[6rem] z-5 bg-white border border-gray-300 rounded-full shadow-lg p-2 flex items-center justify-center transition hover:bg-purple-100"
              aria-label="Rolar para baixo"
              style="box-shadow: 0 2px 8px rgba(80, 0, 120, 0.08);"
            >
              <ChevronDownIcon width="20" height="20" />
            </button>

            <!-- Input para conversar -->
            <div class="flex-none w-full border-t border-gray-200 bg-white">
                <ChatInput
                  v-model="input"
                  :loading="loading || loadingSendingMessage"
                  :textarea-ref="textareaRef"
                  :rows="textareaRows"
                  class="bg-white"
                  @send="handleSend"
                />
            </div>
          </template>

          <ResultDialog
            :show="knowledgeDialogOpen"
            title="Base de conhecimento"
            @close="handleCloseKnowledgeDialog"
          >
            <div v-for="source in knowledgeDialogContent" :key="source.id" class="my-1">
              <span class="text-sm font-bold">{{ source.file_name }}</span>
              <p>{{ source.content }}</p>
            </div>
          </ResultDialog>
        </div>
      </div>
    </transition>
    <Notification
      :show="showNotification"
      :message="notificationMessage"
      :type="notificationType"
    />
  </div>
</template>

<style scoped lang="scss">
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.chat-container {
  color: #e0d7f3;
}

.scroll-down-btn {
  transition: background 0.2s;
}
.scroll-down-btn:hover {
  background: #ede7f6;
}

.scroll-down-btn {
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  width: 38px;
  height: 38px;
  margin: 0 auto;
}

.empty-center {
  overflow-y: hidden;
  min-height: calc(100vh - 260px);
  display: flex;
  align-items: center;
  justify-content: start;
}
</style>

<style lang="scss">
/* Estilos GLOBAIS para markdown - SEM SCOPED para v-html */
.markdown-rendered {
  /* Reset para garantir que listas apareçam */
  ol, ul {
    list-style: revert !important;
  }
  
  ol {
    list-style-type: decimal !important;
    list-style-position: outside !important;
    padding-left: 16px !important;
    margin: 0.75rem 0 !important;
    
    li {
      display: list-item !important;
      margin: 0.5rem 0 !important;
      line-height: 1.6 !important;
    }
  }

  ul {
    list-style-type: disc !important;
    list-style-position: outside !important;
    padding-left: 16px !important;
    margin: 0.75rem 0 !important;
    
    li {
      display: list-item !important;
      margin: 0.5rem 0 !important;
      line-height: 1.6 !important;
    }
  }

  p {
    margin: 0.5rem 0 !important;
    line-height: 1.6 !important;
  }

  strong {
    font-weight: 600 !important;
  }

  em {
    font-style: italic !important;
  }

  del {
    text-decoration: line-through !important;
  }

  code {
    background-color: #f3f4f6 !important;
    color: #1f2937 !important;
    padding: 0.125rem 0.375rem !important;
    border-radius: 0.25rem !important;
    font-family: 'Courier New', monospace !important;
    font-size: 0.9em !important;
  }

  pre { 
    width: fit-content !important;
    padding: 4px 0px !important;   
    code {
    display: flex !important;
    background-color: #f3f4f6 !important;
    color: #1f2937 !important;
    padding: 0.125rem !important;
    border-radius: 0.25rem !important;
    font-family: 'Courier New', monospace !important;
    font-size: 0.9em !important;
    }
  }

  blockquote {
    border-left: 3px solid #7e67f0 !important;
    padding-left: 1rem !important;
    margin: 0.75rem 0 !important;
    color: #6b7280 !important;
    font-style: italic !important;
  }

  /* Tables */
  table {
    width: 100% !important;
    border-collapse: separate !important;
    border-spacing: 0 !important;
    border: 1px solid #dcddde !important; /* gray-200 */
    border-radius: 6px !important;
    overflow: hidden !important;
    margin: 0.75rem 0 !important;
    font-size: 0.9rem !important;
    color: #1f2937 !important; /* gray-800 */
  }

  thead {
    background-color: #F0F2F4 !important; /* gray-50 */
    color: #374151 !important; /* gray-700 */
  }

  th, td {
    text-align: left !important;
    vertical-align: top !important;
    padding: 0.5rem 0.75rem !important;
    border-right: 1px solid #dcddde !important; /* vertical separators */
    border-bottom: 1px solid #dcddde !important; /* horizontal separators */
  }
  td {
    background-color: #F8FAFC !important;
  }

  /* Remove extra borders on the last column and last row to avoid double border with table */
  tr > th:last-child,
  tr > td:last-child {
    border-right: 0 !important;
  }

  tbody tr:last-child > td,
  thead tr:last-child > th {
    border-bottom: 0 !important;
  }

  /* Ensure code wraps nicely inside table cells */
  td code, th code {
    white-space: pre-wrap !important;
  }
}
</style>